import type { ApiClient, CheckoutSession, Transaction } from 'grabbitbot-legacy-ui'
import type { ChargeRequest, DefaultConfig, PlanQuote, ServiceAccount } from './types'

const SERVICE_ACCOUNTS_PATTERN = /^\/serviceAccounts/
const PAYMENT_PATTERN = /^\/payment/
const CREDITS_PATTERN = /^\/credits/

/**
 * GrabbitBot-specific domain client — the B1-scope pieces the shared
 * `grabbitbot-legacy-ui` `paymentClient` deliberately does not carry (that
 * library only ports the *current* payment-generation contract), plus the
 * v3 exchange-picker read and `/serviceAccounts/*`. Ported from
 * `V3ExchangeService`/`PaymentService`/`ServiceAccountService`
 * (`src/app/data/api-client/v3/v3-exchange.service.ts`,
 * `src/app/data/api-client/payment.service.ts`,
 * `src/app/data/api-client/service-account.service.ts` in the legacy
 * archive) — see legacy-migration docs/audit/components/
 * secure.grabbitbot.com.md §4/§6.4.
 */
export function createPortalClient(client: ApiClient) {
  return {
    /** `V3ExchangeService.loadExchanges` -> `V3ConfigService.searchDefault`.
     * `tradingType` is always `'spot'` in round 1 (owner 2026-09-26:
     * marginbot is not rewritten — the legacy client's own `tradingTypes`
     * list was `['spot']` only). */
    searchDefaultConfigs: (tradingType: string) =>
      client.get<DefaultConfig[]>('/v3/{tradingType}/config/default', { params: { tradingType } }),

    /** B5 — `POST /payment/startonline`: an OFF-SESSION CHARGE of the saved
     * card at the payment service (legacy `PaymentService.payPlan` /
     * `payProduct` / `payCredit` -> `payWithData`). BUSINESS-CRITICAL.
     * `idempotencyKey` is REQUIRED here (ticket 4fc3bfc4, contract §5-6):
     * the backend derives the payment service's key from it, so a retry of
     * the SAME submit with the SAME key resumes / replays the same charge
     * instead of charging again. Never retried automatically. Answers the
     * `Transaction` export (a decline is a transaction with `status: 9`,
     * never an HTTP error), or `null`. */
    startOnline: (data: ChargeRequest, idempotencyKey: string) =>
      client.post<Transaction | null>('/payment/startonline', data, {
        headers: { 'Idempotency-Key': idempotencyKey },
        invalidates: [PAYMENT_PATTERN, CREDITS_PATTERN],
      }),

    /** `GET /payment/quote` (0.15.0): read-only preview of buying `plan`,
     * optionally REPLACING the running plan `replacesPlan` (owner decisions
     * 2026-10-13). Never cached: the balances move. 501 `quote_unavailable`
     * = the payment service is older than 0.15.0 (keep the plain flow). */
    quotePlan: (plan: string, replacesPlan?: string) =>
      client.get<PlanQuote>('/payment/quote', { query: { plans: plan, replacesPlan }, cache: false }),

    /** `POST /payment/sessions` for ONE plan, optionally replacing a running
     * plan. The shared library's `createPlanSession` cannot carry
     * `replacesPlan` (a GrabbitBot-only flow for now), so this posts the same
     * legacy body plus that one field. The session charges only the card
     * remainder; a fully covered purchase is 409
     * `fully_covered_use_startonline` (pay with `startOnline` instead). */
    createPlanSession: (cause: string, plan: string, opts: { replacesPlan?: string; oldSessionId?: string } = {}) =>
      client.post<CheckoutSession>(
        '/payment/sessions',
        { cause, type: 'plan', plans: plan, replacesPlan: opts.replacesPlan, oldSessionId: opts.oldSessionId },
        { invalidates: [PAYMENT_PATTERN] },
      ),

    searchServiceAccountRoles: () => client.get<string[]>('/serviceAccounts/roles'),
    searchServiceAccounts: () => client.get<ServiceAccount[]>('/serviceAccounts'),
    /** `POST /serviceAccounts` generates the `login` and the secret itself
     * (B1 contract note §24-30, `app/routers/service_accounts.py`
     * `add_service_account`) — the request body only ever accepts `roles`
     * (only `"all"` is a known role), and the optional `description`/
     * `comment` labels. There is no client-supplied `login`. */
    addServiceAccount: (data: { roles: string[]; description?: string; comment?: string }) =>
      client.post<ServiceAccount>('/serviceAccounts', data, { invalidates: [SERVICE_ACCOUNTS_PATTERN] }),
    /** Deliberate, permanent `410` (round-1 port plan, B1: "new random
     * bcrypt secrets, `/pass` -> 410 as in the pilot") — the new backend
     * never stores a recoverable secret. Callers must treat any non-2xx
     * here as "not retrievable", not just a transient failure. */
    viewServiceAccountPassword: (id: string) =>
      client.get<{ password: string }>('/serviceAccounts/{id}/pass', { params: { id }, cache: false }),
    modifyServiceAccount: (id: string, data: Partial<ServiceAccount>) =>
      client.patch<ServiceAccount>('/serviceAccounts/{id}', data, {
        params: { id },
        invalidates: [SERVICE_ACCOUNTS_PATTERN],
      }),
    revokeServiceAccount: (id: string) =>
      client.delete<unknown>('/serviceAccounts/{id}', { params: { id }, invalidates: [SERVICE_ACCOUNTS_PATTERN] }),
  }
}

export type PortalClient = ReturnType<typeof createPortalClient>
